import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ListFilterToolbar from 'src/common/components/list-filters/ListFilterToolbar';
import ToolbarFilterSelect from 'src/common/components/list-filters/ToolbarFilterSelect';
import { hasActiveSelectFilter } from 'src/helpers/listFilters';
import { EOrder } from 'src/interfaces/Order';
import { EView } from 'src/interfaces/View';
import useFileManagerStore from 'src/stores/fileManagerStore';

type SortBy = 'createdAt' | 'fileName' | 'fileSize';

type MediaManagerToolbarProps = {
  view: EView;
  onSetView: (view: EView) => void;
  /** Inline header row (e.g. media modal) — no full-width grid or horizontal scroll. */
  compact?: boolean;
};

const MediaManagerToolbar = ({ view, onSetView, compact = false }: MediaManagerToolbarProps) => {
  const { t } = useTranslation();
  const search = useFileManagerStore(state => state.search);
  const isUsedFilter = useFileManagerStore(state => state.isUsedFilter);
  const sortBy = useFileManagerStore(state => state.sortBy);
  const sortOrder = useFileManagerStore(state => state.sortOrder);
  const setSearch = useFileManagerStore(state => state.setSearch);
  const setIsUsedFilter = useFileManagerStore(state => state.setIsUsedFilter);
  const setSortBy = useFileManagerStore(state => state.setSortBy);
  const setSortOrder = useFileManagerStore(state => state.setSortOrder);

  const sortOptions = useMemo(
    () => [
      { label: t('system.fileManager.sort.date'), value: 'createdAt' },
      { label: t('system.fileManager.sort.name'), value: 'fileName' },
      { label: t('system.fileManager.sort.size'), value: 'fileSize' },
    ],
    [t],
  );

  const hasActiveFilters = hasActiveSelectFilter(isUsedFilter);

  return (
    <ListFilterToolbar
      popupId="mediaManagerFilters"
      searchQuery={search}
      onSearchChange={setSearch}
      searchPlaceholder={t('system.listFilters.searchPlaceholder')}
      hasActiveFilters={hasActiveFilters}
      filterPopoverWidth={360}
      filterPopoverContent={
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid size={12}>
            <ToolbarFilterSelect
              id="media-manager-usage-filter"
              label={t('system.fileManager.usageStatus')}
              value={isUsedFilter}
              allValue="all"
              onChange={setIsUsedFilter}
            >
              <MenuItem value="all">{t('base.all')}</MenuItem>
              <MenuItem value="true">{t('system.fileManager.used')}</MenuItem>
              <MenuItem value="false">{t('system.fileManager.unused')}</MenuItem>
            </ToolbarFilterSelect>
          </Grid>
        </Grid>
      }
      order={sortOrder as EOrder}
      orderByKey={sortBy}
      setOrder={setSortOrder}
      setOrderByKey={key => setSortBy(key as SortBy)}
      sortLabel={t('sorter.order')}
      sortOptions={sortOptions}
      view={view}
      onSetView={onSetView}
      compact={compact}
    />
  );
};

export default MediaManagerToolbar;
